import React, { useState, useEffect, useRef } from "react";
import { BiRefresh } from "react-icons/bi";
import * as url from "../../../../utils/url";
import axios from "../../../../axios";
import ChatMessage from "./ChatMessage";

export default function TriviaSMSResponses({ trivia }) {
  const chatRef = useRef();
  const [isOpen, setIsOpen] = useState(!!trivia.comingFromNeedsAttention);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]); // [{ id, senderType, text, createdAt }]
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false); // avoid refetching every toggle

  // Guard: only show for messaging trivvys
  if (!trivia || !trivia.isMessaging) return null;

  const triviaResultId = trivia._id;

  // Helper: get the user's original message text from the trivia result
  const getOriginalMessageText = function(t) {
    if (!t) return "";
    // TODO: adjust these keys to match your real result schema
    if (t.messageText && typeof t.messageText === "string")
      return t.messageText;
    if (t.text && typeof t.text === "string") return t.text;
    if (t.freeText && typeof t.freeText === "string") return t.freeText;
    return "";
  };

  // When the component first mounts, seed history with the user's original message
  useEffect(
    function() {
      const orig = getOriginalMessageText(trivia);
      if (orig) {
        setHistory([
          {
            id: "original",
            senderType: "USER",
            text: orig,
            createdAt: trivia.createdAt || null,
          },
        ]);
      }
    },
    [trivia]
  );



  useEffect(() => {
    if (!!trivia.comingFromNeedsAttention) {
      fetchConversation();
    }
  }, []);

  const handleToggle = function() {
    const next = !isOpen;
    setIsOpen(next);

    // Lazy-load conversation from backend the first time the panel is opened
    if (next && !hasFetched && !loading) {
      fetchConversation();
    }
  };

  const fetchConversation = async function() {
    if (!triviaResultId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(url.getTriviaConversation(triviaResultId));
      setHasFetched(true);

      // If backend returns success + messages, map them in
      if (res && res.data && res.data.success) {
        const data = res.data;

        if (data.messages && data.messages.length > 0) {
          const msgs = data.messages.map(function(m) {
            return {
              id: m._id,
              senderType: m.senderType, // "USER" | "ADMIN" | "SYSTEM"
              text: m.body,
              createdAt: m.createdAt,
            };
          });

          setHistory(function(prev) {
            // If we already seeded the original user message at index 0,
            // avoid duplicating it. Assume backend doesn't include that synthetic one.
            if (prev.length > 0 && prev[0].id === "original") {
              return prev.concat(msgs);
            }
            return msgs;
          });

          // Scroll to bottom after loading conversation
          setTimeout(() => {
            if (chatRef.current) {
              chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
          }, 0);
        }
        // If success but no messages, that's fine — no prior conversation.
        return;
      }

      // If we get here, it means res.data.success is false or missing.
      // For now, just treat it as "no conversation" instead of an error.
      // If you later add a specific error code, you can branch on that here.
      // Do NOT set an error message in this case.
    } catch (err) {
      console.error("Error fetching conversation", err);

      // If backend returns 404 for "no conversation yet", don't show an error.
      if (err.response && err.response.status === 404) {
        setHasFetched(true);
        // no history change; keep the seeded original message
      } else {
        // Real failure (500, network, etc)
        setError("Failed to load conversation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async function() {
    const trimmed = (message || "").trim();
    if (!trimmed || sending || !triviaResultId) return;

    setSending(true);
    setError(null);

    // Optimistic UI: append an ADMIN message locally
    const tempId = "local-" + Date.now();
    const newMsg = {
      id: tempId,
      senderType: "ADMIN",
      text: trimmed,
      createdAt: new Date().toISOString(),
      pending: true,
    };

    setHistory(function(prev) {
      return prev.concat(newMsg);
    });
    setMessage("");

    // Scroll to bottom after adding new message
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 0);

    try {
      // Adjust to your actual API endpoint and payload
      const res = await axios.post(url.replyToTriviaResult, {
        triviaResultId: triviaResultId,
        replyText: trimmed,
        channel: "AUTO",
      });

      if (res && res.data && res.data.success) {
        const data = res.data;

        // If backend returns the saved message, replace the temp one
        if (data.message && data.message._id) {
          setHistory(function(prev) {
            return prev.map(function(m) {
              if (m.id === tempId) {
                return {
                  id: data.message._id,
                  senderType: data.message.senderType || "ADMIN",
                  text: data.message.body || trimmed,
                  createdAt: data.message.createdAt || m.createdAt,
                };
              }
              return m;
            });
          });
        } else {
          // Otherwise, just drop the "pending" flag
          setHistory(function(prev) {
            return prev.map(function(m) {
              if (m.id === tempId) {
                return {
                  id: m.id,
                  senderType: m.senderType,
                  text: m.text,
                  createdAt: m.createdAt,
                };
              }
              return m;
            });
          });
        }
      } else {
        // On failure, remove the optimistic message and show error
        setHistory(function(prev) {
          return prev.filter(function(m) {
            return m.id !== tempId;
          });
        });
        setError("Failed to send message.");
      }
    } catch (err) {
      console.error("Error sending reply", err);
      setHistory(function(prev) {
        return prev.filter(function(m) {
          return m.id !== tempId;
        });
      });
      setError("Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const renderSenderLabel = function(senderType) {
    const userName =
      trivia.user && !trivia.isAnonymous
        ? `${trivia.user.firstName} ${trivia.user.lastName}`
        : "Anonymous user";

    if (senderType === "USER") {
      return userName;
    }
    if (senderType === "ADMIN") {
      return "Admin";
    }
    return "System";
  };

  return (
    <div>
      <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
        <button
          type="button"
          className="share_page_btn_color btn btn-secondary"
          onClick={handleToggle}
        >
          {isOpen ? "Hide" : "Respond"}
        </button>
        {isOpen && (
          <span
            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#64748b" }}
            onClick={function() {
              setHistory([]);
              setHasFetched(false);
              fetchConversation();
            }}
            title="Refresh chat"
          >
            <BiRefresh size={16} /> Refresh chat
          </span>
        )}
      </div>

      {isOpen && (
        <div style={{ marginTop: "1rem" }}>
          {/* History / loading / errors */}
          {loading ? (
            <div className="text-muted mb-2">Loading conversation…</div>
          ) : (
            <div
              ref={chatRef}
              className="chat-history mb-2"
              style={{
                maxHeight: "250px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                padding: "0.5rem",
              }}
            >
              {history.length === 0 ? (
                <div className="text-muted text-center">No messages yet.</div>
              ) : (
                history.map(function(item, idx) {
                  return (
                    <ChatMessage
                      key={item.id || idx}
                      item={item}
                      renderSenderLabel={renderSenderLabel}
                    />
                  );
                })
              )}
            </div>
          )}

          {error && <div className="alert alert-danger py-1 mb-2">{error}</div>}

          {/* Textarea + send */}
          <textarea
            className="form-control"
            rows={3}
            value={message}
            onChange={function(e) {
              setMessage(e.target.value);
            }}
            placeholder="Type your response…"
          />
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? "Sending…" : "Reply"}
          </button>
        </div>
      )}
    </div>
  );
}
